import { useState, useEffect, useRef } from 'react';
import { Navigate, useLocation, useParams } from 'react-router';

import { FeatureFlag } from '@/shared/api/protocol-ts/model/dto_organization_pb';
import { useAppSelector, useMedia } from '@/shared/hooks';
import { Layout, Spinner } from '@/shared/ui';

import { organizationModel } from '@/entities/organization';
import { patientModel } from '@/entities/patient';

import { Header, MobileHeader } from '@/widgets/Header';
import { PatientDocuments } from '@/widgets/PatientDocuments';

import styles from './PatientDocumentPage.module.scss';

export const PatientDocumentPage = () => {
  const { isMobile } = useMedia();
  const [documentTitle, setDocumentTitle] = useState('');
  const mobileHeaderRef = useRef<HTMLDivElement>(null);
  const { search } = useLocation();
  const { patientID = '', documentID = '' } = useParams();
  const { CanEditPatient } = useAppSelector(
    patientModel.selectors.selectPatientYourPermissions(patientID),
  );
  const enabled = useAppSelector(
    organizationModel.selectors.selectFeatureFlag(
      FeatureFlag.FeatureFlag_Enable_AgentChat,
    ),
  );
  const settingsLoaded = useAppSelector(
    organizationModel.selectors.selectCurrentOrganizationSettingsLoaded,
  );
  useEffect(() => {
    if (!settingsLoaded || !enabled || !isMobile) {
      return;
    }
    const header = mobileHeaderRef.current;
    if (!header) {
      return;
    }
    const measure = () =>
      document.documentElement.style.setProperty(
        '--actualHeaderHeight',
        `${header.getBoundingClientRect().height}px`,
      );
    const observer = new ResizeObserver(measure);
    observer.observe(header);
    measure();
    return () => observer.disconnect();
  }, [settingsLoaded, enabled, isMobile]);
  if (!settingsLoaded) {
    return <Spinner />;
  }
  if (!enabled) {
    return (
      <Navigate to={{ pathname: `/patients/${patientID}`, search }} replace />
    );
  }
  return (
    <Layout fullWidth className={styles.page}>
      <Layout.Header>
        {isMobile ? (
          <div ref={mobileHeaderRef} className={styles.mobileHeader}>
            <MobileHeader currentPageLabel={documentTitle} />
          </div>
        ) : (
          <Header fullWidth currentPageLabel={documentTitle} />
        )}
      </Layout.Header>
      <Layout.Content>
        <PatientDocuments
          key={patientID}
          patientID={patientID}
          documentID={documentID}
          canEdit={CanEditPatient}
          onTitleChange={setDocumentTitle}
        />
      </Layout.Content>
    </Layout>
  );
};
